import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData } from '../data/types';
import { ScreenSection, ScreenSparkBars } from '../primitives/ScreenPrimitives';
import { ScreenLineChart, type LineChartPoint } from '../primitives/ChartPrimitives';
import { overviewColors, overviewDesign, newsDesign, competitorsDesign, coverFonts, ptToPx, interSectionGap } from '../data/designTokens';
import { fmtMonthYear, fmtMonthYearShort } from './formatUtils';
import { stripHtml, splitBullets, truncateText, sourceInitials, sentimentTone } from '../data/overviewHelpers';

// ─── Color resolver ─────────────────────────────────────────────
// Tokens reference colour names (string keys). resolve() turns them into hex.
type ColorKey = keyof typeof overviewColors;
const c = (key: string): string => (overviewColors as Record<string, string>)[key] ?? key;

// ─── View model ─────────────────────────────────────────────────
type ChipTone = 'positive' | 'accent' | 'muted';
type Chip = { label: string; tone: ChipTone };
type SectorMetric = { label: string; value: string; unit?: string; sub?: string; valueColor?: string };

type OverviewVM = {
    initial: string;
    orgTitle: string;
    chips: Chip[];
    subtitle: string;
    aboutBullets?: string[];
    businessCards: Array<{ title: string; bullets: string[] }>;
    basicDetails: {
        items: Array<{ label: string; value: string; href?: string }>;
        address?: string;
    };
    news: Array<{
        url?: string;
        source: string;
        initials: string;
        sentiment: 'pos' | 'neg' | 'neutral';
        sentimentLabel: string;
        headline: string;
        date: string;
    }>;
    sectorOutlook?: {
        sectorName?: string;
        intro?: string;
        metrics: SectorMetric[];
        body?: string;
        growthDrivers: string[];
        keyRisks: string[];
    };
    competitors?: {
        focal: { name: string };
        rivals: Array<{ name: string }>;
        rows: Array<{ key: string; label: string; values: string[]; mono?: boolean }>;
        cards: Array<{
            name: string;
            kind: 'subject' | 'competitor';
            description?: string;
            truncated?: boolean;
            foundingYear?: string;
            location?: string;
            website?: string;
            keyAchievements?: string;
        }>;
    };
};

const buildVM = (data: ReportData): OverviewVM => {
    const o = data.overview;
    const id = data.identity;
    const score = data.score;

    const initial = (id.orgName ?? '?').trim()[0]?.toUpperCase() ?? '?';

    const chips: Chip[] = [];
    if (id.profileStatus) chips.push({ label: id.profileStatus, tone: 'positive' });
    if (id.startupSme) chips.push({ label: id.startupSme, tone: 'accent' });

    const subtitleParts: string[] = [];
    if (id.foundationDate) subtitleParts.push(`Founded ${fmtMonthYear(id.foundationDate)}`);
    if (id.sector) subtitleParts.push(id.sector);
    if (id.state) subtitleParts.push(id.state);
    const subtitle = subtitleParts.join(' · ');

    const aboutBullets = o?.overviewContent ? splitBullets(stripHtml(o.overviewContent)) : undefined;

    const businessCards: OverviewVM['businessCards'] = [];
    if (o?.productOfferings) businessCards.push({ title: 'Product & Offerings', bullets: splitBullets(stripHtml(o.productOfferings)) });
    if (o?.customersAndSalesChannels)
        businessCards.push({ title: 'Customers & Channels', bullets: splitBullets(stripHtml(o.customersAndSalesChannels)) });
    if (o?.geographiesSegmentation)
        businessCards.push({ title: 'Geographies', bullets: splitBullets(stripHtml(o.geographiesSegmentation)) });

    // Basic Details. `href` (optional per row) is rendered as a clickable
    // hyperlink in the PDF — used for the company website. Static rows like
    // PAN / CIN / GSTIN don't get a href so they render as plain mono text.
    const bdItems: Array<{ label: string; value: string; href?: string }> = [];
    if (id.pan && id.pan !== '—') bdItems.push({ label: 'PAN', value: id.pan });
    if (id.cin && id.cin !== '—') bdItems.push({ label: 'CIN', value: id.cin });
    const primaryGstin = id.gstins?.[0]?.gstin;
    if (primaryGstin) bdItems.push({ label: 'GSTIN', value: primaryGstin });
    if (id.websiteUrl) {
        const display = id.websiteUrl.replace(/^https?:\/\//i, '').replace(/\/$/, '');
        bdItems.push({ label: 'Website', value: display, href: id.websiteUrl });
    }
    const basicDetails = { items: bdItems, address: id.fullAddress };

    // News
    const news = (o?.news ?? []).slice(0, 4).map((n) => ({
        url: n.redirectUrl,
        source: truncateText(n.publisherName, 22),
        initials: sourceInitials(n.publisherName),
        sentiment: sentimentTone(n.sentiment),
        sentimentLabel: (n.sentiment ?? '').toUpperCase() || 'NEUTRAL',
        headline: truncateText(stripHtml(n.title), 90),
        date: fmtMonthYear(n.publishedDate) || n.publishedDate,
    }));

    // Sector outlook
    let sectorOutlook: OverviewVM['sectorOutlook'];
    if (o?.sectorOverview || o?.marketSize || o?.industryCagr || o?.marketGrowth || o?.sectorRisks) {
        const metrics: SectorMetric[] = [];
        if (o.marketSize) {
            const parsed = parseMetric(o.marketSize);
            metrics.push({ label: 'Market Size', ...parsed });
        }
        if (o.industryCagr) {
            const parsed = parseMetric(o.industryCagr);
            metrics.push({ label: 'Industry CAGR', ...parsed });
        }
        if (o.sectorRisks) {
            metrics.push({ label: 'Sector Risk', value: capFirst(o.sectorRisks), valueColor: riskValueColor(o.sectorRisks) });
        }
        if (o.marketGrowth) {
            const parsed = parseMetric(o.marketGrowth);
            metrics.push({ label: 'Market Growth', ...parsed });
        }
        sectorOutlook = {
            sectorName: id.sector,
            intro: o.sectoralPerformance ? stripHtml(o.sectoralPerformance) : undefined,
            metrics,
            body: o.sectorOverview ? stripHtml(o.sectorOverview) : undefined,
            growthDrivers: (o.growthDrivers ?? []).map(stripHtml).filter(Boolean).slice(0, 4),
            keyRisks: (o.keyRisks ?? []).map(stripHtml).filter(Boolean).slice(0, 4),
        };
    }

    // Competitors
    let competitors: OverviewVM['competitors'];
    // The Polus `competitors` payload tags the subject company itself as one
    // of the entries (description contains "Subject Company"). We prepend our
    // own focal card below, so we must remove the subject from the rival list
    // first — otherwise the same company renders twice (as Card 1 the subject,
    // again as Card 2 the first "rival"). Belt-and-suspenders: also filter by
    // name match against the subject org name in case the description tag is
    // missing for a given row.
    const subjectNameNorm = (id.orgName ?? '').trim().toLowerCase();
    const allCompetitors = (o?.competitors ?? []).filter((c) => {
        if (/subject\s+company/i.test(c.description ?? '')) return false;
        const name = (c.companyName ?? '').trim().toLowerCase();
        if (subjectNameNorm && name && name === subjectNameNorm) return false;
        return true;
    });
    if (allCompetitors.length > 0) {
        const focal = { name: id.orgName };
        const rivals = allCompetitors.slice(0, 3).map((cm) => ({ name: cm.companyName }));
        // The Polus competitor endpoint emits `keyAchievements` for rival
        // companies but the subject company comes from a separate
        // `companyOverview` endpoint that doesn't expose that field directly.
        // Synthesize one from existing signals so the subject card matches
        // the visual weight of its rivals: prefer the first recent trend
        // (the closest semantic match), fall back to a funding-raised
        // summary, then to the top-line product offering. Return undefined
        // when none of those produce content — leaving the slot empty is
        // better than printing filler.
        const synthesizeKeyAchievements = (): string | undefined => {
            const firstTrend = (o?.recentTrends ?? []).map(stripHtml).filter(Boolean)[0];
            if (firstTrend) return truncateText(firstTrend, 220);
            const funding = (o?.fundingRaised ?? '').trim();
            const topInvestors = (o?.keyInvestors ?? []).slice(0, 3).filter(Boolean).join(', ');
            if (funding && topInvestors) return `Raised ${funding} with backing from ${topInvestors}.`;
            if (funding) return `Total funding raised: ${funding}.`;
            const offering = o?.productOfferings ? stripHtml(o.productOfferings) : '';
            if (offering) return truncateText(offering, 220);
            return undefined;
        };
        const focalCompetitor = {
            companyName: id.orgName,
            description: aboutBullets?.join(' '),
            // Show just the state name (matches the AICA Overview tab). The
            // small competitor card can't fit a full HQ address — long strings
            // wrap awkwardly and steal space from the rest of the card.
            location: id.state,
            revenue: undefined as string | undefined,
            employees: data.management?.employeeRange,
            foundingYear: (() => {
                if (!id.foundationDate) return undefined;
                const y = new Date(id.foundationDate).getFullYear();
                return Number.isFinite(y) ? y.toString() : undefined;
            })(),
            totalFunding: o?.fundingRaised,
            fundingStage: id.startupSme,
            investors: (o?.keyInvestors ?? []).slice(0, 3).join(', '),
            website: id.websiteUrl,
            keyAchievements: synthesizeKeyAchievements(),
        };
        const cols = [focalCompetitor, ...allCompetitors.slice(0, 3)];
        const rowSpec: Array<{ key: string; label: string; mono?: boolean; pick: (cm: any) => string | undefined }> = [
            { key: 'about', label: 'About', pick: (cm) => cm.description },
            { key: 'location', label: 'Location', pick: (cm) => extractState(cm.location) },
            { key: 'revenue', label: 'Revenue', mono: true, pick: (cm) => cm.revenue },
            { key: 'employees', label: 'Employees', mono: true, pick: (cm) => cm.employees },
            { key: 'founded', label: 'Founded', mono: true, pick: (cm) => cm.foundingYear },
            { key: 'funding', label: 'Funding', mono: true, pick: (cm) => cm.totalFunding },
            // 'Stage' / 'Funding Stage' row removed per design feedback —
            // the value (id.startupSme) is rarely populated and read as
            // noise next to the other rows. Re-add the row spec entry to
            // surface it again.
            { key: 'investors', label: 'Investors', pick: (cm) => cm.investors },
        ];
        const rows = rowSpec
            .map((r) => ({
                key: r.key,
                label: r.label,
                mono: r.mono,
                values: cols.map((cm) => {
                    const v = r.pick(cm);
                    if (!v || v === '—' || v.toLowerCase() === 'nan') return 'Not disclosed';
                    return r.key === 'about' ? truncateText(v, 140) : v;
                }),
            }))
            .filter((r) => r.values.some((v) => v !== 'Not disclosed'));
        const cards = cols.map((cm, i) => {
            const dropFiller = (v?: string) => (!v || /not publicly disclosed|not disclosed|nan/i.test(v) ? undefined : v);
            // Truncate the description so the SUBJECT card (whose description is
            // the full About-bullets joined together — easily 1000+ chars) does
            // not overflow its wrap={false} card and spill into the next column.
            // Cap is generous enough for a real competitor description (~200
            // chars in the API), short enough to fit the card's `descMinHeight`
            // line budget. `truncateText` adds "…" as the explicit indicator;
            // the full About text already lives in the "About the Company"
            // section above, so a truncated marker here is reader-safe.
            const description = cm.description ? truncateText(cm.description, 260) : undefined;
            return {
                name: cm.companyName ?? '—',
                kind: (i === 0 ? 'subject' : 'competitor') as 'subject' | 'competitor',
                description,
                truncated: !!(cm.description && cm.description.length > 260),
                foundingYear: dropFiller(cm.foundingYear),
                location: dropFiller(extractState(cm.location)),
                website: dropFiller(cm.website),
                keyAchievements: dropFiller(cm.keyAchievements),
            };
        });
        competitors = { focal, rivals, rows, cards };
    }

    return {
        initial,
        orgTitle: id.orgName ?? '—',
        chips,
        subtitle,
        aboutBullets,
        businessCards,
        basicDetails,
        news,
        sectorOutlook,
        competitors,
    };
};

// Parse a sector-outlook metric value into its three visual parts:
//   - `value`: the headline number (mono medium, primary ink)
//   - `unit`:  the currency / scale (mono regular, muted ink, sits next to value)
//   - `sub`:   trailing context like "by 2028" or "(US$28 Bn)" — separate line, smaller
//
// Examples:
//   "Rs. 2,43,236 Cr (US$28 Bn)" → { value: 'Rs. 2,43,236', unit: 'Cr', sub: 'US$28 Bn' }
//   "₹ 5,179 Cr by 2028"         → { value: '₹ 5,179',      unit: 'Cr', sub: 'by 2028' }
//   "10-11% annually"            → { value: '10-11%',       sub: 'annually' }
//   "15%"                        → { value: '15%' }
// Replace currency / scale glyphs that GeistMono (the value font) lacks. When a
// font has no glyph for a code point, react-pdf either drops it to zero advance
// width (next char overlaps) or substitutes a tofu — either way the metric reads
// as garbage like "T9 billion" instead of "₹9 billion". Mono fonts in particular
// rarely ship Indian currency / extended Latin glyphs, so we ASCII-fy on the way
// in. Keep this list narrow; only add a symbol here after seeing it actually
// fail to render.
const sanitizeForMonoFont = (s: string): string =>
    s
        .replace(/₹\s*/g, 'Rs. ')
        .replace(/\s{2,}/g, ' ')
        .trim();

const parseMetric = (raw: string): { value: string; unit?: string; sub?: string } => {
    const trimmed = sanitizeForMonoFont(raw);

    // 1. Pull off any trailing parenthetical — that's always sub.
    const parenMatch = trimmed.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    let main = trimmed;
    let sub: string | undefined;
    if (parenMatch) {
        main = parenMatch[1].trim();
        sub = parenMatch[2].trim();
    }

    // 2. Split off "VALUE UNIT [TRAILING SUB]" where unit is one of the known scale words.
    const unitMatch = main.match(/^(.+?)\s+(Cr|Crore|Lakh|Mn|Bn|Million|Billion)(?:\s+(.+))?$/i);
    if (unitMatch) {
        const value = unitMatch[1].trim();
        const unit = unitMatch[2];
        const trailing = unitMatch[3]?.trim();
        return { value, unit, sub: sub ?? trailing };
    }

    // 3. Percent at the end of the value, followed by trailing sub: "10-11% annually".
    const pctMatch = main.match(/^([\d.,\-\s]+%)\s+(.+)$/);
    if (pctMatch) {
        return { value: pctMatch[1].trim(), sub: sub ?? pctMatch[2].trim() };
    }

    return { value: main, sub };
};

const capFirst = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s);

// Reduce a long HQ location string (Polus shape "City, State, Country") to
// just the state. The Key Competitors card can't fit a full address — long
// strings wrap and steal vertical space from the rest of the card. Heuristic:
//   - strip trailing country tokens (India, USA, UK, etc.)
//   - if 2+ segments remain after the strip, return the last (state)
//   - if 1 segment remains, return as-is
//   - if the input is already short (<= ~22 chars), return as-is — it's
//     probably already just a state name (the focal card's source).
const COUNTRY_TOKENS = /^(india|usa|us|united states|uk|united kingdom|england|china|singapore|uae|canada|australia)$/i;
const extractState = (loc?: string): string | undefined => {
    if (!loc) return loc;
    const trimmed = loc.trim();
    if (trimmed.length <= 22 && !trimmed.includes(',')) return trimmed;
    const parts = trimmed
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    if (parts.length === 0) return trimmed;
    const withoutCountry = COUNTRY_TOKENS.test(parts[parts.length - 1]) ? parts.slice(0, -1) : parts;
    if (withoutCountry.length === 0) return parts[0];
    return withoutCountry[withoutCountry.length - 1];
};

const riskValueColor = (s: string): string => {
    const v = s.toLowerCase();
    if (v.includes('high') || v.includes('severe')) return overviewColors.neg;
    if (v.includes('medium')) return overviewColors.warn;
    return overviewColors.pos;
};

const od = overviewDesign;
const nd = newsDesign;
const cd = competitorsDesign;

// Employee headcount chart. Website Traffic was previously stacked below this
// but has been removed (signal was noisy and the SimilarWeb data wasn't load-
// bearing for the credit narrative).
const buildEmployeePoints = (data: ReportData): LineChartPoint[] => {
    const mom = data.management?.momEmployee ?? [];
    return mom.slice(-18).map((x) => ({ label: fmtMonthYearShort(x.yearMonth), value: x.count }));
};

// ════════════════════════════════════════════════════════════════
//                          S C R E E N
// ════════════════════════════════════════════════════════════════

const SF = coverFonts.screen;

const ScreenH3: React.FC<{ children: React.ReactNode; topGap?: number }> = ({ children, topGap = od.sectionGap }) => (
    <div
        style={{
            fontSize: ptToPx(od.h3Size),
            fontFamily: SF.sans,
            fontWeight: 600,
            color: c(od.h3Color),
            marginTop: ptToPx(topGap),
            marginBottom: ptToPx(od.headingToBody),
        }}
    >
        {children}
    </div>
);

const ScreenChipBox: React.FC<{ label: string; tone: ChipTone }> = ({ label, tone }) => {
    const tones: Record<ChipTone, { bg: string; fg: string }> = {
        positive: { bg: overviewColors.posSoft, fg: overviewColors.pos },
        accent: { bg: overviewColors.accentSoft, fg: overviewColors.accentDeep },
        muted: { bg: overviewColors.surfaceAlt, fg: overviewColors.inkSubtle },
    };
    const t = tones[tone];
    return (
        <span
            style={{
                background: t.bg,
                paddingLeft: ptToPx(od.idChipPadX),
                paddingRight: ptToPx(od.idChipPadX),
                paddingTop: ptToPx(od.idChipPadY),
                paddingBottom: ptToPx(od.idChipPadY + 1),
                borderRadius: ptToPx(od.idChipRadius),
                fontSize: ptToPx(od.idChipSize),
                fontFamily: SF.sans,
                fontWeight: 600,
                color: t.fg,
                letterSpacing: `${od.idChipTrackingEm}em`,
                textTransform: 'uppercase',
            }}
        >
            {label}
        </span>
    );
};

const ScreenIdentityCard: React.FC<{ vm: OverviewVM }> = ({ vm }) => (
    <div
        style={{
            background: overviewColors.surface,
            borderRadius: ptToPx(od.idCardRadius),
            paddingLeft: ptToPx(od.idCardPadX),
            paddingRight: ptToPx(od.idCardPadX),
            paddingTop: ptToPx(od.idCardPadY),
            paddingBottom: ptToPx(od.idCardPadY),
            display: 'flex',
            alignItems: 'center',
            gap: ptToPx(od.idCardGap),
        }}
    >
        <div
            style={{
                width: ptToPx(od.idLogoSize),
                height: ptToPx(od.idLogoSize),
                borderRadius: ptToPx(od.idLogoRadius),
                background: overviewColors.ink,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <span style={{ fontSize: ptToPx(od.idLogoTextSize), color: '#FFFFFF', fontFamily: SF.display, lineHeight: 1 }}>
                {vm.initial}
            </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: ptToPx(od.idChipGap), flexWrap: 'wrap' }}>
                <span
                    style={{
                        fontSize: ptToPx(od.idTitleSize),
                        fontFamily: SF.display,
                        color: overviewColors.ink,
                        lineHeight: od.idTitleLineHeight,
                    }}
                >
                    {vm.orgTitle}
                </span>
                {vm.chips.map((ch) => (
                    <ScreenChipBox key={ch.label} label={ch.label} tone={ch.tone} />
                ))}
            </div>
            {vm.subtitle ? (
                <div
                    style={{
                        fontSize: ptToPx(od.idSubtitleSize),
                        fontFamily: SF.sans,
                        color: c(od.idSubtitleColor),
                        marginTop: 4,
                        lineHeight: od.idSubtitleLineHeight,
                    }}
                >
                    {vm.subtitle}
                </div>
            ) : null}
        </div>
    </div>
);

const ScreenBusinessCard: React.FC<{ title: string; bullets: string[] }> = ({ title, bullets }) => (
    <div
        style={{
            flex: 1,
            background: '#fafaf8',
            border: `${ptToPx(od.bpCardBorder)}px solid ${overviewColors.border}`,
            borderRadius: ptToPx(od.bpCardRadius),
            padding: ptToPx(od.bpCardPad),
        }}
    >
        <div
            style={{
                fontSize: ptToPx(od.bpTitleSize),
                fontFamily: SF.sans,
                fontWeight: 600,
                color: c(od.bpTitleColor),
                marginBottom: 6,
            }}
        >
            {title}
        </div>
        {bullets.map((b, i) => (
            <div
                key={i}
                style={{
                    display: 'flex',
                    gap: 5,
                    marginTop: i > 0 ? 4 : 0,
                    fontSize: ptToPx(od.bpBodySize),
                    fontFamily: SF.sans,
                    color: c(od.bpBodyColor),
                    lineHeight: od.bpBodyLineHeight,
                }}
            >
                <span>•</span>
                <span style={{ flex: 1 }}>{b}</span>
            </div>
        ))}
    </div>
);

const ScreenBasicDetails: React.FC<{ vm: OverviewVM['basicDetails'] }> = ({ vm }) => (
    <div
        style={{
            padding: 12,
            borderRadius: 8,
            border: `0.5px solid ${overviewColors.border}`,
            background: '#fafaf8',
        }}
    >
        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 10, rowGap: 8 }}>
            {vm.items.map((it) => (
                <div
                    key={it.label}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '7px 14px 8px',
                        borderRadius: 999,
                        border: `0.5px solid ${overviewColors.border}`,
                        background: '#FFFFFF',
                    }}
                >
                    <span
                        style={{
                            fontSize: ptToPx(od.bdLabelSize),
                            fontFamily: SF.sans,
                            fontWeight: 500,
                            color: c(od.bdLabelColor),
                            letterSpacing: `${od.bdLabelTrackingEm}em`,
                            textTransform: 'uppercase',
                            lineHeight: 1,
                        }}
                    >
                        {it.label}:
                    </span>
                    {it.href ? (
                        <a
                            href={it.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                fontSize: ptToPx(od.bdValueSize),
                                fontFamily: SF.mono,
                                fontWeight: 500,
                                color: overviewColors.accent,
                                textDecoration: 'none',
                                lineHeight: 1,
                            }}
                        >
                            {it.value}
                        </a>
                    ) : (
                        <span
                            style={{
                                fontSize: ptToPx(od.bdValueSize),
                                fontFamily: SF.mono,
                                fontWeight: 500,
                                color: c(od.bdValueColor),
                                lineHeight: 1,
                            }}
                        >
                            {it.value}
                        </span>
                    )}
                </div>
            ))}
        </div>
        {vm.address ? (
            // Inline-block + width:fit-content so the address pill shrinks to
            // its text width instead of stretching to fill the container.
            <div style={{ marginTop: 10, display: 'flex' }}>
                <span
                    style={{
                        padding: '7px 14px 8px',
                        borderRadius: 999,
                        border: `0.5px solid ${overviewColors.border}`,
                        background: '#FFFFFF',
                        fontSize: ptToPx(od.bdAddressSize),
                        fontFamily: SF.sans,
                        color: c(od.bdAddressColor),
                        lineHeight: od.bdAddressLineHeight,
                        display: 'inline-block',
                    }}
                >
                    {vm.address}
                </span>
            </div>
        ) : null}
    </div>
);

const ScreenNewsCard: React.FC<{ item: OverviewVM['news'][number] }> = ({ item }) => {
    const sentColors = {
        pos: { bg: overviewColors.posSoft, fg: overviewColors.pos },
        neg: { bg: overviewColors.negSoft, fg: overviewColors.neg },
        neutral: { bg: overviewColors.surfaceAlt, fg: overviewColors.inkSubtle },
    }[item.sentiment];
    return (
        <div
            style={{
                flex: 1,
                background: '#FFFFFF',
                border: `${ptToPx(nd.cardBorder)}px solid ${overviewColors.border}`,
                borderRadius: ptToPx(nd.cardRadius),
                padding: ptToPx(nd.cardPad),
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: ptToPx(nd.sourceGap) }}>
                    <span
                        style={{
                            width: ptToPx(nd.sourcePillSize),
                            height: ptToPx(nd.sourcePillSize),
                            borderRadius: '50%',
                            background: c(nd.sourcePillBg),
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: ptToPx(nd.sourcePillTextSize),
                            color: c(nd.sourcePillTextColor),
                            fontFamily: SF.sans,
                            fontWeight: 600,
                        }}
                    >
                        {item.initials}
                    </span>
                    <span
                        style={{
                            fontSize: ptToPx(nd.sourceLabelSize),
                            color: c(nd.sourceLabelColor),
                            fontFamily: SF.sans,
                            fontWeight: 600,
                        }}
                    >
                        {item.source}
                    </span>
                </div>
                <span
                    style={{
                        background: sentColors.bg,
                        paddingLeft: ptToPx(nd.sentChipPadX),
                        paddingRight: ptToPx(nd.sentChipPadX),
                        paddingTop: ptToPx(nd.sentChipPadY),
                        paddingBottom: ptToPx(nd.sentChipPadY + 1),
                        borderRadius: ptToPx(nd.sentChipRadius),
                        fontSize: ptToPx(nd.sentChipSize),
                        fontFamily: SF.sans,
                        fontWeight: 600,
                        color: sentColors.fg,
                        letterSpacing: `${nd.sentChipTrackingEm}em`,
                        textTransform: 'uppercase',
                    }}
                >
                    {item.sentimentLabel}
                </span>
            </div>
            <div
                style={{
                    marginTop: 5,
                    fontSize: ptToPx(nd.headlineSize),
                    fontFamily: SF.sans,
                    fontWeight: 500,
                    color: c(nd.headlineColor),
                    lineHeight: nd.headlineLineHeight,
                }}
            >
                {item.url ? (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#6021B3', textDecoration: 'underline' }}>
                        {item.headline}
                    </a>
                ) : (
                    item.headline
                )}
            </div>
            <div style={{ marginTop: 4, fontSize: ptToPx(nd.dateSize), fontFamily: SF.mono, color: c(nd.dateColor) }}>{item.date}</div>
        </div>
    );
};

const ScreenSectorMetric: React.FC<{ m: SectorMetric }> = ({ m }) => (
    <div
        style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: '#FFFFFF',
            border: `${ptToPx(nd.cardBorder)}px solid ${overviewColors.border}`,
            borderRadius: ptToPx(nd.cardRadius),
            paddingLeft: ptToPx(nd.sectorMetricPad),
            paddingRight: ptToPx(nd.sectorMetricPad),
            paddingTop: 8,
            paddingBottom: 8,
            minHeight: ptToPx(60),
        }}
    >
        <div
            style={{
                fontSize: ptToPx(nd.sectorMetricLabelSize),
                fontFamily: SF.sans,
                fontWeight: 600,
                color: c(nd.sectorMetricLabelColor),
                letterSpacing: `${nd.sectorMetricLabelTrackingEm}em`,
                textTransform: 'uppercase',
            }}
        >
            {m.label}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, marginTop: 4 }}>
            <span
                style={{
                    fontSize: ptToPx(nd.sectorMetricValueSize),
                    fontFamily: SF.mono,
                    fontWeight: 500,
                    color: m.valueColor ?? c(nd.sectorMetricValueColor),
                }}
            >
                {m.value}
            </span>
            {m.unit ? (
                <span style={{ fontSize: ptToPx(nd.sectorMetricUnitSize), fontFamily: SF.mono, color: c(nd.sectorMetricUnitColor) }}>
                    {m.unit}
                </span>
            ) : null}
        </div>
        {m.sub ? (
            <div
                style={{
                    fontSize: ptToPx(nd.sectorMetricSubSize - 1),
                    fontFamily: SF.sans,
                    color: c(nd.sectorMetricSubColor),
                    marginTop: 'auto',
                    paddingTop: 4,
                    textAlign: 'right',
                }}
            >
                {m.sub}
            </div>
        ) : null}
    </div>
);

const ScreenDriverList: React.FC<{ items: string[]; header: string; tone: 'green' | 'red' }> = ({ items, header, tone }) => {
    const headerColor = tone === 'green' ? c(nd.drGreenColor) : c(nd.drRedColor);
    return (
        <div
            style={{
                flex: 1,
                background: c(nd.drCardBg),
                borderRadius: ptToPx(nd.drCardRadius),
                padding: ptToPx(nd.drCardPad),
            }}
        >
            <div
                style={{
                    fontSize: ptToPx(nd.drHeaderSize),
                    fontFamily: SF.sans,
                    fontWeight: 500,
                    color: headerColor,
                    letterSpacing: `${nd.drHeaderTrackingEm}em`,
                    textTransform: 'uppercase',
                    marginBottom: 6,
                }}
            >
                {header}
            </div>
            {items.map((item, i) => (
                <div
                    key={i}
                    style={{
                        display: 'flex',
                        gap: 9,
                        paddingTop: ptToPx(nd.drListItemPadY),
                        paddingBottom: ptToPx(nd.drListItemPadY),
                        borderBottom: i < items.length - 1 ? `${ptToPx(nd.drListBorder)}px solid ${c(nd.drListBorderColor)}` : 'none',
                    }}
                >
                    <span style={{ fontSize: ptToPx(nd.drDashSize), fontFamily: SF.display, color: c(nd.drDashColor), lineHeight: 1 }}>
                        —
                    </span>
                    <span
                        style={{ flex: 1, fontSize: ptToPx(nd.drItemSize), fontFamily: SF.sans, color: c(nd.drItemColor), lineHeight: 1.5 }}
                    >
                        {item}
                    </span>
                </div>
            ))}
        </div>
    );
};

const ScreenCompetitorsCards: React.FC<{ vm: NonNullable<OverviewVM['competitors']> }> = ({ vm }) => {
    const cards = vm.cards;
    if (cards.length === 0) return null;
    // Screen-side tighter line-height for the name + description text. PDF
    // keeps the shared `cd.cardNameLineHeight` / `cd.cardDescLineHeight` tokens
    // because react-pdf renders at its own typographic metrics; the screen
    // renders larger and looser by default and needs to be reined in here.
    const screenNameLineHeight = 1.1;
    const screenDescLineHeight = 1.25;
    const nameMinHeight = ptToPx(cd.cardNameSize * screenNameLineHeight * cd.cardNameMaxLines);
    const descMinHeight = ptToPx(cd.cardDescSize * screenDescLineHeight * cd.cardDescMaxLines);
    const rows: ((typeof cards)[number] | null)[][] = [];
    for (let i = 0; i < cards.length; i += 2) rows.push([cards[i], cards[i + 1] ?? null]);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: ptToPx(cd.cardGap) }}>
            {rows.map((row, ri) => (
                <div key={ri} style={{ display: 'flex', gap: ptToPx(cd.cardGap) }}>
                    {row.map((card, ci) => {
                        if (!card) return <div key={ci} style={{ flex: 1 }} />;
                        const subject = card.kind === 'subject';
                        const leftBorder = subject ? cd.cardSubjectBorderLeft : cd.cardBorder;
                        const padX = cd.cardPad - 1.5;
                        const padY = cd.cardPad - 2.5;
                        return (
                            <div
                                key={ci}
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    background: subject ? c(cd.cardSubjectBg) : '#FFFFFF',
                                    borderTop: `${ptToPx(cd.cardBorder)}px solid ${c(cd.cardBorderColor)}`,
                                    borderRight: `${ptToPx(cd.cardBorder)}px solid ${c(cd.cardBorderColor)}`,
                                    borderBottom: `${ptToPx(cd.cardBorder)}px solid ${c(cd.cardBorderColor)}`,
                                    borderLeft: `${ptToPx(leftBorder)}px solid ${
                                        subject ? c(cd.cardSubjectBorderColor) : c(cd.cardBorderColor)
                                    }`,
                                    borderRadius: ptToPx(cd.cardRadius),
                                    paddingTop: ptToPx(padY),
                                    paddingBottom: ptToPx(padY),
                                    paddingRight: ptToPx(padX),
                                    paddingLeft: ptToPx(subject ? padX + cd.cardSubjectBorderLeft - cd.cardBorder : padX),
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <div style={{ minHeight: nameMinHeight }}>
                                    <span
                                        style={{
                                            fontSize: ptToPx(cd.cardNameSize),
                                            fontFamily: SF.sans,
                                            fontWeight: 600,
                                            color: c(cd.cardNameColor),
                                            lineHeight: screenNameLineHeight,
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {card.name}
                                    </span>
                                </div>
                                {subject ? (
                                    <div
                                        style={{
                                            fontSize: ptToPx(cd.cardKickerSize),
                                            fontFamily: SF.sans,
                                            fontWeight: 500,
                                            color: c(cd.cardKickerSubjectColor),
                                            letterSpacing: `${cd.cardKickerTrackingEm}em`,
                                            textTransform: 'uppercase',
                                            marginTop: ptToPx(cd.cardKickerGap),
                                        }}
                                    >
                                        Subject Company
                                    </div>
                                ) : null}
                                <div style={{ marginTop: ptToPx(subject ? cd.cardDescGap : cd.cardKickerGap), minHeight: descMinHeight }}>
                                    {card.description ? (
                                        <span
                                            style={{
                                                fontSize: ptToPx(cd.cardDescSize),
                                                fontFamily: SF.sans,
                                                color: c(cd.cardDescColor),
                                                lineHeight: screenDescLineHeight,
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {card.description}
                                        </span>
                                    ) : null}
                                    {card.truncated ? (
                                        <div
                                            style={{
                                                fontSize: ptToPx(cd.cardDescSize - 0.5),
                                                fontStyle: 'italic',
                                                color: overviewColors.inkMuted,
                                                marginTop: 4,
                                            }}
                                        >
                                            Truncated — see "About the Company" above for the full text.
                                        </div>
                                    ) : null}
                                </div>
                                <div
                                    style={{
                                        borderTop: `${ptToPx(cd.cardBorder)}px solid ${cd.cardDividerColor}`,
                                        marginTop: ptToPx(6),
                                    }}
                                >
                                    {(
                                        [
                                            { label: 'Founded', value: card.foundingYear },
                                            { label: 'Location', value: card.location },
                                            { label: 'Website', value: card.website, href: card.website },
                                        ] as Array<{ label: string; value?: string; href?: string }>
                                    )
                                        .filter((r) => !!r.value)
                                        .map((r, idx, arr) => (
                                            <div
                                                key={r.label}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    paddingTop: ptToPx(cd.cardRowPadY),
                                                    paddingBottom: ptToPx(cd.cardRowPadY),
                                                    borderBottom:
                                                        idx === arr.length - 1
                                                            ? 'none'
                                                            : `${ptToPx(cd.cardBorder)}px solid ${cd.cardDividerColor}`,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: ptToPx(cd.cardRowSize),
                                                        fontFamily: SF.sans,
                                                        color: c(cd.cardRowLabelColor),
                                                    }}
                                                >
                                                    {r.label}
                                                </span>
                                                {r.href ? (
                                                    <a
                                                        href={r.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            fontSize: ptToPx(cd.cardRowSize),
                                                            fontFamily: SF.mono,
                                                            fontWeight: 500,
                                                            color: overviewColors.accent,
                                                            textDecoration: 'none',
                                                        }}
                                                    >
                                                        {(r.value ?? '').replace(/^https?:\/\//i, '').replace(/\/$/, '')}
                                                    </a>
                                                ) : (
                                                    <span
                                                        style={{
                                                            fontSize: ptToPx(cd.cardRowSize),
                                                            fontFamily: SF.mono,
                                                            fontWeight: 500,
                                                            color: c(cd.cardRowValueColor),
                                                        }}
                                                    >
                                                        {r.value}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                </div>
                                {card.keyAchievements ? (
                                    <div style={{ marginTop: ptToPx(6) }}>
                                        <div
                                            style={{
                                                fontSize: ptToPx(cd.cardKickerSize),
                                                fontFamily: SF.sans,
                                                fontWeight: 600,
                                                color: c(cd.cardRowLabelColor),
                                                letterSpacing: `${cd.cardKickerTrackingEm}em`,
                                                textTransform: 'uppercase',
                                                marginBottom: ptToPx(3),
                                            }}
                                        >
                                            Key Achievements
                                        </div>
                                        <span
                                            style={{
                                                fontSize: ptToPx(cd.cardDescSize),
                                                fontFamily: SF.sans,
                                                color: c(cd.cardDescColor),
                                                lineHeight: cd.cardDescLineHeight,
                                            }}
                                        >
                                            {card.keyAchievements}
                                        </span>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

// Legacy comparison table — kept in source so we can swap back via the
// render line below if the new card layout doesn't fit a future spec.
const ScreenCompetitorsTable: React.FC<{ vm: NonNullable<OverviewVM['competitors']> }> = ({ vm }) => {
    const cols = [vm.focal.name, ...vm.rivals.map((r) => r.name)];
    return (
        <div
            style={{
                borderTop: `${ptToPx(cd.headerBorder)}px solid ${c(cd.headerBorderColor)}`,
                borderBottom: `${ptToPx(cd.headerBorder)}px solid ${c(cd.headerBorderColor)}`,
            }}
        >
            <div style={{ display: 'flex' }}>
                <div style={{ width: ptToPx(cd.labelColWidth) }} />
                {cols.map((name, i) => {
                    const focal = i === 0;
                    return (
                        <div
                            key={i}
                            style={{
                                flex: 1,
                                background: focal ? c(cd.focalHeaderBg) : '#FFFFFF',
                                borderBottom: `${ptToPx(cd.headerBorder)}px solid ${
                                    focal ? c(cd.focalHeaderBorder) : c(cd.headerBorderColor)
                                }`,
                                paddingLeft: ptToPx(cd.cellPadX),
                                paddingRight: ptToPx(cd.cellPadX),
                                paddingTop: ptToPx(cd.cellPadY + 4),
                                paddingBottom: ptToPx(cd.cellPadY + 4),
                            }}
                        >
                            <span
                                style={{
                                    fontSize: ptToPx(cd.headerSize),
                                    fontFamily: SF.sans,
                                    fontWeight: 600,
                                    color: focal ? c(cd.headerFocalColor) : c(cd.headerColor),
                                    letterSpacing: `${cd.headerTrackingEm}em`,
                                    textTransform: 'uppercase',
                                }}
                            >
                                {name}
                            </span>
                        </div>
                    );
                })}
            </div>
            {vm.rows.map((row) => (
                <div key={row.key} style={{ display: 'flex' }}>
                    <div
                        style={{
                            width: ptToPx(cd.labelColWidth),
                            paddingLeft: ptToPx(cd.cellPadX),
                            paddingRight: ptToPx(cd.cellPadX),
                            paddingTop: ptToPx(cd.cellPadY),
                            paddingBottom: ptToPx(cd.cellPadY),
                            borderBottom: `${ptToPx(cd.border)}px solid ${c(cd.borderColor)}`,
                        }}
                    >
                        <span
                            style={{
                                fontSize: ptToPx(cd.labelSize),
                                fontFamily: SF.sans,
                                fontWeight: 500,
                                color: c(cd.labelColor),
                                letterSpacing: `${cd.labelTrackingEm}em`,
                                textTransform: 'uppercase',
                            }}
                        >
                            {row.label}
                        </span>
                    </div>
                    {row.values.map((value, i) => {
                        const focal = i === 0;
                        const muted = value === 'Not disclosed';
                        return (
                            <div
                                key={i}
                                style={{
                                    flex: 1,
                                    background: focal ? c(cd.focalBg) : '#FFFFFF',
                                    paddingLeft: ptToPx(cd.cellPadX),
                                    paddingRight: ptToPx(cd.cellPadX),
                                    paddingTop: ptToPx(cd.cellPadY),
                                    paddingBottom: ptToPx(cd.cellPadY),
                                    borderBottom: `${ptToPx(cd.border)}px solid ${c(cd.borderColor)}`,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: ptToPx(cd.cellSize),
                                        fontFamily: row.mono ? SF.mono : SF.sans,
                                        color: muted ? c(cd.cellMutedColor) : c(cd.cellColor),
                                        lineHeight: cd.cellLineHeight,
                                    }}
                                >
                                    {value}
                                </span>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

const ScreenOverview: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    // No early bail on missing `overview`. Section renders whenever any sub-block
    // has data (gated by `hasData` on the module).
    const vm = buildVM(data);

    return (
        <ScreenSection kicker="Company" title="Company Overview" index={index}>
            <ScreenIdentityCard vm={vm} />

            {vm.aboutBullets && vm.aboutBullets.length > 0 ? (
                <>
                    <ScreenH3>About the Company</ScreenH3>
                    {vm.aboutBullets.map((b, i) => (
                        <div
                            key={i}
                            style={{
                                display: 'flex',
                                gap: 6,
                                marginTop: i > 0 ? 4 : 0,
                                fontSize: ptToPx(od.bodySize),
                                fontFamily: SF.sans,
                                color: c(od.bodyColor),
                                lineHeight: od.bodyLineHeight,
                            }}
                        >
                            <span>•</span>
                            <span style={{ flex: 1 }}>{b}</span>
                        </div>
                    ))}
                </>
            ) : null}

            {vm.businessCards.length > 0 ? (
                <>
                    <ScreenH3>Business Profile</ScreenH3>
                    <div style={{ display: 'flex', gap: ptToPx(od.bpGap) }}>
                        {vm.businessCards.map((card) => (
                            <ScreenBusinessCard key={card.title} title={card.title} bullets={card.bullets} />
                        ))}
                    </div>
                </>
            ) : null}

            {vm.basicDetails.items.length > 0 ? (
                <>
                    <ScreenH3>Basic Details</ScreenH3>
                    <ScreenBasicDetails vm={vm.basicDetails} />
                </>
            ) : null}

            {vm.news.length > 0 ? (
                <div style={{ marginTop: ptToPx(interSectionGap) }}>
                    <ScreenH3 topGap={0}>News &amp; Updates</ScreenH3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: ptToPx(nd.gridGap) }}>
                        {vm.news.map((n, i) => (
                            <ScreenNewsCard key={i} item={n} />
                        ))}
                    </div>
                </div>
            ) : null}

            {vm.sectorOutlook ? (
                <div style={{ marginTop: ptToPx(interSectionGap) }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 8,
                            marginBottom: ptToPx(od.headingToBody),
                        }}
                    >
                        <span style={{ fontSize: ptToPx(od.h3Size), fontFamily: SF.sans, fontWeight: 600, color: c(od.h3Color) }}>
                            Sector / Industry Outlook
                        </span>
                        {vm.sectorOutlook.sectorName ? (
                            <span style={{ fontSize: ptToPx(7.5), fontFamily: SF.sans, color: overviewColors.inkMuted }}>
                                {vm.sectorOutlook.sectorName}
                            </span>
                        ) : null}
                    </div>
                    {vm.sectorOutlook.metrics.length > 0 ? (
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${vm.sectorOutlook.metrics.length}, 1fr)`,
                                gap: ptToPx(nd.gridGap),
                                marginBottom: ptToPx(nd.sectorGap),
                            }}
                        >
                            {vm.sectorOutlook.metrics.map((m) => (
                                <ScreenSectorMetric key={m.label} m={m} />
                            ))}
                        </div>
                    ) : null}
                    {vm.sectorOutlook.body ? (
                        <div
                            style={{
                                fontSize: ptToPx(nd.sectorBodySize),
                                fontFamily: SF.sans,
                                color: c(nd.sectorBodyColor),
                                lineHeight: nd.sectorBodyLineHeight,
                                marginBottom: ptToPx(nd.sectorGap),
                            }}
                        >
                            {vm.sectorOutlook.body}
                        </div>
                    ) : null}
                    {vm.sectorOutlook.growthDrivers.length > 0 || vm.sectorOutlook.keyRisks.length > 0 ? (
                        <div style={{ display: 'flex', gap: ptToPx(nd.gridGap) }}>
                            {vm.sectorOutlook.growthDrivers.length > 0 ? (
                                <ScreenDriverList items={vm.sectorOutlook.growthDrivers} header="Growth Drivers" tone="green" />
                            ) : null}
                            {vm.sectorOutlook.keyRisks.length > 0 ? (
                                <ScreenDriverList items={vm.sectorOutlook.keyRisks} header="Key Risks" tone="red" />
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {vm.competitors ? (
                <div style={{ marginTop: ptToPx(interSectionGap) }}>
                    <ScreenH3 topGap={0}>Key Competitors</ScreenH3>
                    <ScreenCompetitorsCards vm={vm.competitors} />
                </div>
            ) : null}
            <ScreenOverviewMiniCharts data={data} />
        </ScreenSection>
    );
};

const ScreenOverviewMiniCharts: React.FC<{ data: ReportData }> = ({ data }) => {
    const emp = buildEmployeePoints(data);
    if (emp.length < 2) return null;
    return (
        <div style={{ marginTop: ptToPx(interSectionGap) }}>
            <div>
                <ScreenH3 topGap={0}>Employee Headcount</ScreenH3>
                <ScreenLineChart points={emp} height={140} />
                <div style={{ fontSize: '0.7rem', color: overviewColors.inkMuted, marginTop: 4 }}>
                    Monthly EPF-registered employees · last 18 months
                </div>
            </div>
        </div>
    );
};

export const overviewModule: ModuleDefinition = {
    meta: {
        id: 'overview',
        title: 'Company overview',
        description: 'Company identity, business profile, basic details, risk grade, news, sector outlook and competitors.',
        dataSource: 'Profile Overview, Sector Outlook, News, Competitors',
    },
    hasData: (d) => {
        if (!d.identity?.orgName) return false;
        const o = d.overview;
        const hasNarrative = !!(o?.overviewContent || o?.productOfferings || o?.customersAndSalesChannels || o?.geographiesSegmentation);
        const hasNews = (o?.news?.length ?? 0) > 0;
        const hasCompetitors = (o?.competitors?.length ?? 0) > 0;
        const hasSectorOutlook = !!(
            o?.sectorOverview ||
            o?.marketSize ||
            o?.industryCagr ||
            o?.marketGrowth ||
            o?.sectorRisks ||
            o?.growthDrivers?.length ||
            o?.keyRisks?.length
        );
        const hasHeadcount = (d.management?.momEmployee?.length ?? 0) >= 2;
        return hasNarrative || hasNews || hasCompetitors || hasSectorOutlook || hasHeadcount;
    },
    Screen: ScreenOverview,
};
